import { useState, useCallback } from "react";
import { exportToCSV } from "../../utils/dashboardSharedUtils";
import { authFetch, authHeaders } from "../../utils/authFetch";

const API_ROOT_BASE = (() => {
  const envBase = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  if (envBase) {
    const base = envBase.endsWith("/api") ? envBase.slice(0, -4) : envBase;
    return `${base.replace(/\/+$/, "")}/api`;
  }
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:8000/api";
    }
    return `${window.location.origin}/api`;
  }
  return "https://app.neurolonic.com/api";
})();

export default function useStudentImportExport({ students, userDetails, t, onImportComplete }) {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const exportStudentsToCSV = useCallback(() => {
    if (!students.length) {
      alert(t("No students to export"));
      return;
    }

    const headers = [
      "Name",
      "Class",
      "Section",
      "Age",
      "Gender",
      "Contact",
      "Assigned Staff ID",
      "Last Session",
      "Calm",
      "Stressed",
      "Demotivated",
      "Anxious",
      "Hurt",
      "Burnout Score",
    ];

    const rows = students.map((s) => [
      s.name || "",
      s.class || "",
      s.section || "",
      s.age || "",
      s.gender || "",
      s.contact || "",
      s.assigned_staff_id || "",
      s.last_session || "",
      s.eve?.calm ?? 0,
      s.eve?.stressed ?? 0,
      s.eve?.demotivated ?? 0,
      s.eve?.anxious ?? 0,
      s.eve?.hurt ?? 0,
      s.burnout_score ?? 0,
    ]);

    exportToCSV(headers, rows, "students_export");
  }, [students, t]);

  const handleImportStudentsCSV = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.name.endsWith(".csv")) {
        alert(t("Please select a CSV file"));
        return;
      }
      setImporting(true);
      setImportResult(null);
      try {
        const userId =
          userDetails?.user_id || localStorage.getItem("neuro_user_id");
        const formData = new FormData();
        formData.append("file", file);
        const res = await authFetch(
          `${API_ROOT_BASE}/school/import-students?university_id=${encodeURIComponent(userId || "")}`,
          {
            method: "POST",
            headers: authHeaders(),
            body: formData,
          },
        ).then((r) => r.json());
        if (res.status_code === 200) {
          setImportResult({
            success: true,
            count: res.data?.length || 0,
            message: res.message,
          });
          if (onImportComplete) onImportComplete();
        } else {
          setImportResult({
            success: false,
            message: res.message || "Import failed",
          });
        }
      } catch (err) {
        setImportResult({ success: false, message: "Failed to import" });
      }
      setImporting(false);
      e.target.value = null;
    },
    [userDetails, t, onImportComplete],
  );

  return {
    importing,
    importResult,
    exportStudentsToCSV,
    handleImportStudentsCSV,
  };
}
